import { IconCheck, IconChevronDown, IconCopy } from "@tabler/icons-react";
import { useState, type ReactNode } from "react";
import type {
  ProFeeCodeEvidence,
  ProFeeCodeRow,
  ProFeeCodingResult,
} from "../../../types/pro-fee-coding-results";
import { copyPlainText } from "../../../utils/encoder";
import {
  codeRowKey,
  defaultOpenDiagnosisKey,
  isNewPatientEmCode,
  labelChipStyle,
  procedureUnitsDisplay,
  splitCodeLabels,
} from "../../../utils/pro-fee-coding-results";

type SectionKind = "diagnosis" | "cpt" | "em";

const PILL_TITLES: Record<SectionKind, string> = {
  diagnosis: "DIAGNOSIS REVIEW RESULTS",
  cpt: "CPT CODE RESULTS",
  em: "E/M CODE RESULTS",
};

const CODE_LABELS: Record<SectionKind, string> = {
  diagnosis: "ICD-10",
  cpt: "CPT",
  em: "E/M",
};

/** Read-only snapshot control — muted so it reads as frozen, not editable. */
const FrozenCheckbox = ({
  checked,
  label,
}: {
  checked: boolean;
  label: string;
}) => (
  <span
    role="checkbox"
    aria-checked={checked}
    aria-label={label}
    aria-disabled
    className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border ${
      checked
        ? "border-gray-400 bg-gray-200 text-gray-600"
        : "border-gray-300 bg-gray-100"
    } cursor-not-allowed opacity-70`}
  >
    {checked ? <IconCheck size={14} stroke={2.5} /> : null}
  </span>
);

const PdxBadge = () => (
  <span
    className="inline-flex items-center rounded-sm px-1.5 py-px text-[11px] font-bold leading-none text-white"
    style={{ backgroundColor: "#1d4ed8" }}
  >
    PDx
  </span>
);

const LabelChip = ({ label }: { label: string }) => {
  const colors = labelChipStyle(label);
  return (
    <span
      className="inline-flex items-center rounded-sm px-1.5 py-px text-[10px] font-semibold"
      style={{ backgroundColor: colors.background, color: colors.color }}
    >
      {label}
    </span>
  );
};

const UnitsChip = ({ value }: { value: string }) => (
  <span className="mt-0.5 inline-flex h-4 min-w-[22px] items-center justify-center rounded border border-emerald-400 bg-white px-1 text-[10px] font-semibold leading-none text-emerald-700">
    {value}
  </span>
);

const YellowField = ({
  children,
  className = "",
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) => (
  <div
    title={title}
    className={`flex h-7 min-h-0 items-center rounded border border-gray-300 bg-yellow-50 px-1.5 text-xs text-gray-800 ${className}`}
  >
    {children}
  </div>
);

const EvidenceCopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:bg-white/70 hover:text-gray-800"
      aria-label={copied ? "Copied evidence" : "Copy evidence"}
      title={copied ? "Copied" : "Copy"}
      onClick={async (event) => {
        event.stopPropagation();
        const ok = await copyPlainText(text);
        if (!ok) return;
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      }}
    >
      <IconCopy size={14} />
    </button>
  );
};

const EvidenceTable = ({
  evidences,
}: {
  evidences: ProFeeCodeEvidence[];
}) => {
  const headerTint = "bg-gray-100";
  const rowTint = "bg-white";
  const joinedText = evidences.map((item) => item.text).join("\n");
  const columns = "28px minmax(0, 1fr) 160px 112px 52px";

  return (
    <div className="text-xs">
      <div
        className={`${headerTint} grid border-b border-gray-200 text-[12px] font-semibold text-gray-700`}
        style={{ gridTemplateColumns: columns }}
      >
        <div />
        <div className="flex items-center gap-1 py-1.5 pl-1 pr-2">
          <span>Evidence from Medical Record</span>
          {evidences.length ? <EvidenceCopyButton text={joinedText} /> : null}
        </div>
        <div className="px-2 py-1.5">File</div>
        <div className="px-2 py-1.5">DOS</div>
        <div className="px-2 py-1.5">Page</div>
      </div>
      {!evidences.length ? (
        <div className={`${rowTint} px-3 py-3 text-sm text-gray-500`}>
          No evidence available
        </div>
      ) : (
        evidences.map((evidence, index) => (
          <div
            key={`${evidence.text}-${index}`}
            className={`${rowTint} grid border-b border-gray-200 text-sm text-gray-700 last:border-b-0`}
            style={{ gridTemplateColumns: columns }}
          >
            <div className="flex items-center justify-center py-1.5">
              <FrozenCheckbox
                checked={evidence.selected === true}
                label={`Evidence ${index + 1} selected`}
              />
            </div>
            <div className="flex min-w-0 items-center gap-1.5 py-1.5 pl-1 pr-2">
              <p className="min-w-0 flex-1 truncate" title={evidence.text}>
                {evidence.text}
              </p>
              {evidence.source === "MANUAL" ? (
                <span
                  className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-fuchsia-600 text-[10px] font-bold text-white"
                  title="Manual evidence"
                >
                  M
                </span>
              ) : null}
            </div>
            <div className="truncate px-2 py-2" title={evidence.file}>
              {evidence.file || "—"}
            </div>
            <div className="truncate px-2 py-2">{evidence.dos || "—"}</div>
            <div className="px-2 py-2">{evidence.page || "—"}</div>
          </div>
        ))
      )}
    </div>
  );
};

const PROCEDURE_GRID =
  "36px 72px minmax(220px, 2fr) 44px 92px minmax(150px, 1fr) 24px";
const DIAGNOSIS_GRID = "36px 72px minmax(180px, 1fr) 72px 24px";

const SectionHeader = ({
  kind,
  procedureColumns,
}: {
  kind: SectionKind;
  procedureColumns?: boolean;
}) => (
  <div className="relative min-h-[38px] border-b border-gray-200 bg-[#f3e8ff]">
    <div
      className="grid items-center gap-x-2 px-3 py-2.5 text-sm font-semibold text-gray-800"
      style={{
        gridTemplateColumns: procedureColumns ? PROCEDURE_GRID : DIAGNOSIS_GRID,
      }}
    >
      <span className="text-center">#</span>
      <span>{CODE_LABELS[kind]}</span>
      <span>DESCRIPTION</span>
      {procedureColumns ? (
        <>
          <span aria-hidden />
          <span className="text-[11px] uppercase tracking-wide">MODIFIER</span>
          <span className="text-[11px] uppercase tracking-wide">DIAGNOSIS</span>
        </>
      ) : (
        <span className="text-center">PDx</span>
      )}
      <span aria-hidden />
    </div>
    <div
      className="pointer-events-none absolute inset-y-0 left-1/2 flex -translate-x-1/2 items-center"
      aria-hidden
    >
      <div className="flex items-center gap-1.5 bg-[#f3e8ff] px-3">
        <img
          src="/ai-bulwark-logo.svg"
          alt=""
          className="h-[18px] w-auto object-contain"
        />
        <span
          className="inline-flex h-[26px] items-center justify-center rounded-md px-3 text-[11px] font-semibold text-white"
          style={{
            backgroundColor: "rgba(130, 12, 165, 0.9)",
            minWidth: 224,
          }}
        >
          {PILL_TITLES[kind]}
        </span>
      </div>
    </div>
  </div>
);

const CodeResultsSection = ({
  kind,
  rows,
  diagnosisDescriptions,
  defaultOpenKey,
}: {
  kind: SectionKind;
  rows: ProFeeCodeRow[];
  diagnosisDescriptions?: Record<string, string>;
  defaultOpenKey?: string | null;
}) => {
  const [openKeys, setOpenKeys] = useState<Record<string, boolean>>(() =>
    defaultOpenKey ? { [defaultOpenKey]: true } : {}
  );
  const isDiagnosis = kind === "diagnosis";
  const columns = isDiagnosis ? DIAGNOSIS_GRID : PROCEDURE_GRID;

  const toggle = (key: string) => {
    setOpenKeys((current) => ({ ...current, [key]: !current[key] }));
  };

  return (
    <section className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
      <SectionHeader kind={kind} procedureColumns={!isDiagnosis} />
      <div className={isDiagnosis ? "min-w-[520px]" : "min-w-[760px]"}>
        {rows.map((row) => {
          const key = codeRowKey(kind, row);
          const open = Boolean(openKeys[key]);
          const labels = splitCodeLabels(row.labels);
          const unitsDisplay = procedureUnitsDisplay(row);
          return (
            <div key={key} className="border-b border-gray-200 last:border-b-0">
              <div
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => toggle(key)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggle(key);
                  }
                }}
                className={`grid w-full cursor-pointer items-start gap-x-2 px-3 py-2 text-left text-sm text-gray-800 ${
                  isDiagnosis ? "bg-sky-50/80" : "bg-white"
                }`}
                style={{ gridTemplateColumns: columns }}
              >
                <span className="self-start pt-0.5 text-center font-medium text-gray-700">
                  {row.index}.
                </span>
                <span className="min-w-0 self-start">
                  <span className="block font-semibold leading-tight text-black">
                    {row.code}
                  </span>
                  {isDiagnosis ? (
                    labels.underCode.length ? (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {labels.underCode.map((label) => (
                          <LabelChip key={label} label={label} />
                        ))}
                      </span>
                    ) : null
                  ) : (
                    <UnitsChip value={unitsDisplay} />
                  )}
                </span>
                <span className="min-w-0 pr-2">
                  <span className={isDiagnosis ? "block" : "line-clamp-2 block"}>
                    {row.description || "—"}
                  </span>
                  {!isDiagnosis ? (
                    <span className="mt-1 flex flex-wrap items-center gap-1">
                      {labels.underDescription.map((label) => (
                        <LabelChip key={label} label={label} />
                      ))}
                      {kind === "em" && isNewPatientEmCode(row.code) ? (
                        <span className="inline-flex rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">
                          New Patient
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="mt-1 flex flex-wrap gap-1">
                      {labels.underDescription.map((label) => (
                        <LabelChip key={label} label={label} />
                      ))}
                    </span>
                  )}
                </span>
                {isDiagnosis ? (
                  <span className="flex items-center justify-end gap-2 pt-0.5">
                    {row.isPdx ? <PdxBadge /> : null}
                    <FrozenCheckbox
                      checked={row.isPdx}
                      label={`${row.code} is principal diagnosis`}
                    />
                  </span>
                ) : (
                  <>
                    <YellowField className="justify-center text-center">
                      {unitsDisplay}
                    </YellowField>
                    <YellowField className="self-start">
                      {row.modifiers.join(", ") || ""}
                    </YellowField>
                    <YellowField
                      className="self-start"
                      title={row.diagnosisMappings
                        .map(
                          (code) =>
                            diagnosisDescriptions?.[code] || code
                        )
                        .join(", ")}
                    >
                      <span className="truncate">
                        Dx :{" "}
                        {row.diagnosisMappings.length
                          ? row.diagnosisMappings.join(" , ")
                          : ""}
                      </span>
                    </YellowField>
                  </>
                )}
                <span className="flex justify-center self-start pt-1">
                  <IconChevronDown
                    size={16}
                    className={`text-gray-500 transition-transform ${
                      open ? "rotate-180" : ""
                    }`}
                  />
                </span>
              </div>
              {open ? (
                <div
                  className={`border-t border-gray-200/90 px-3 pb-2 pt-1.5 ${
                    isDiagnosis ? "bg-sky-50/80" : "bg-white"
                  }`}
                >
                  <div className="overflow-hidden rounded border border-gray-200 bg-white">
                    <EvidenceTable evidences={row.evidences} />
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const CodingResultsTables = ({ result }: { result: ProFeeCodingResult }) => (
  <div className="flex flex-col gap-4">
    {result.diagnoses.length ? (
      <CodeResultsSection
        kind="diagnosis"
        rows={result.diagnoses}
        defaultOpenKey={defaultOpenDiagnosisKey(result.diagnoses)}
      />
    ) : null}
    {result.cptCodes.length ? (
      <CodeResultsSection
        kind="cpt"
        rows={result.cptCodes}
        diagnosisDescriptions={result.diagnosisDescriptions}
      />
    ) : null}
    {result.emCodes.length ? (
      <CodeResultsSection
        kind="em"
        rows={result.emCodes}
        diagnosisDescriptions={result.diagnosisDescriptions}
      />
    ) : null}
  </div>
);

export default CodingResultsTables;
